class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    // The project has both the Dawn header cart icon and the custom mega-header
    // cart icon mounted on some breakpoints. They currently share the historical
    // id `cart-icon-bubble`, so bind every mounted instance instead of only the
    // first querySelector match.
    const cartLinks = document.querySelectorAll('[id="cart-icon-bubble"]');
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      if (cartLink.dataset.ssDrawerBound === 'true') return;
      cartLink.dataset.ssDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState = {}) {
    // A successful add should reveal the drawer immediately. Render every
    // section already returned by /cart/add.js first, open the drawer, then
    // repair any missing fragments in the background. This avoids making the
    // visible cart depend on a second request.
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    const requiredSections = this.getSectionsToRender();
    const sections = { ...(parsedState.sections || {}) };

    const renderSection = (section, html) => {
      if (!html) return false;

      const innerHTML = this.getSectionInnerHTML(html, section.selector);
      if (innerHTML === null) return false;

      // Keep every visible header/cart icon in sync. The custom mega header and
      // Dawn header can coexist in the DOM and use the same legacy id, while
      // getElementById() would update only one of them.
      if (section.id === 'cart-icon-bubble') {
        const cartIcons = document.querySelectorAll('[id="cart-icon-bubble"]');
        if (!cartIcons.length) return false;
        cartIcons.forEach((icon) => {
          icon.innerHTML = innerHTML;
        });
        return true;
      }

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return false;
      sectionElement.innerHTML = innerHTML;
      return true;
    };

    requiredSections.forEach((section) => renderSection(section, sections[section.id]));

    // Re-bind the overlay after #CartDrawer may have been replaced, then open
    // without waiting for any fallback network work.
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.open();

    const missingIds = requiredSections
      .filter((section) => !sections[section.id])
      .map((section) => section.id);

    if (!missingIds.length) return;

    // Defensive repair only. A timeout prevents a slow Shopify section
    // endpoint from holding any UI state hostage.
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeout = controller ? setTimeout(() => controller.abort(), 2500) : null;

    try {
      const separator = routes.cart_url.includes('?') ? '&' : '?';
      const response = await fetch(
        `${routes.cart_url}${separator}sections=${missingIds.join(',')}`,
        {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          ...(controller ? { signal: controller.signal } : {}),
        }
      );
      if (!response.ok) return;

      const freshSections = await response.json();
      requiredSections.forEach((section) => {
        if (freshSections?.[section.id]) renderSection(section, freshSections[section.id]);
      });

      this.classList.remove('is-empty');
      this.querySelector('.drawer__inner')?.classList.remove('is-empty');
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.setHeaderCartIconAccessibility();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('Cart drawer section repair:', error);
    } finally {
      if (timeout) clearTimeout(timeout);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
